import { z } from "zod";

import { API_VERSION } from "./common";
import { ingredientSchema, machineSchema } from "./catalog";
import { trimStringInput } from "./schema-utils";
import {
  MAX_RECIPE_INGREDIENTS,
  componentQuantityModeSchema,
  componentQuantitySchema,
  componentRoleSchema,
  evaporationFractionSchema,
  maxRecipeIngredientsMessage,
  persistedRecipeIngredientModifierFieldsSchema,
  recipeDescriptionSchema,
  recipeIngredientAmountSchema,
  recipeIngredientNotesValueSchema,
  recipeInstructionsSchema,
  recipeNameSchema,
  recipeTypeSchema,
} from "./recipe-domain";

export {
  componentQuantityModeSchema,
  componentRoleSchema,
} from "./recipe-domain";

const optionalQueryString = z.preprocess(
  (value) => (value === "" || value === null ? undefined : value),
  z.string().trim().max(512).optional(),
);

const optionalNonNegativeNumber = z.preprocess(
  (value) =>
    value === undefined || value === null || value === ""
      ? undefined
      : Number(value),
  z.number().finite().nonnegative().optional(),
);

export const recipesQuerySchema = z
  .object({
    cursor: optionalQueryString,
    q: optionalQueryString,
    sort: z.enum(["newest", "oldest"]).default("newest"),
    type: recipeTypeSchema.or(z.literal("all")).default("all"),
    caloriesMin: optionalNonNegativeNumber,
    caloriesMax: optionalNonNegativeNumber,
    limit: z.preprocess(
      (value) =>
        value === undefined || value === null || value === ""
          ? undefined
          : Number(value),
      z.number().int().min(1).max(100).default(30),
    ),
  })
  .strict()
  .refine(
    ({ caloriesMax, caloriesMin }) =>
      caloriesMax === undefined ||
      caloriesMin === undefined ||
      caloriesMin <= caloriesMax,
    {
      message: "Minimum calories cannot exceed maximum calories",
      path: ["caloriesMax"],
    },
  );

export const recipeCollectionQuerySchema = z
  .object({
    cursor: optionalQueryString,
    q: optionalQueryString,
    sort: z.enum(["newest", "oldest"]).default("newest"),
    type: recipeTypeSchema.or(z.literal("all")).default("all"),
    caloriesMin: optionalNonNegativeNumber,
    caloriesMax: optionalNonNegativeNumber,
    owner: z.literal("me").optional(),
    visibility: z.literal("public").optional(),
    createdBy: optionalQueryString,
    ingredient: optionalQueryString,
    quality: z.preprocess(
      (value) => value === true || value === "true",
      z.boolean().default(false),
    ),
    limit: z.preprocess(
      (value) =>
        value === undefined || value === null || value === ""
          ? undefined
          : Number(value),
      z.number().int().min(1).max(100).default(30),
    ),
  })
  .strict()
  .superRefine((query, context) => {
    if (
      query.caloriesMax !== undefined &&
      query.caloriesMin !== undefined &&
      query.caloriesMin > query.caloriesMax
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Minimum calories cannot exceed maximum calories",
        path: ["caloriesMax"],
      });
    }
    if (query.owner && query.visibility) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "owner and visibility cannot be combined",
        path: ["owner"],
      });
    }
    if (!query.owner && !query.visibility) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either owner=me or visibility=public is required",
        path: ["owner"],
      });
    }
    if (
      (query.createdBy || query.ingredient || query.quality) &&
      !query.visibility
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Public catalog filters require visibility=public",
        path: ["visibility"],
      });
    }
  });

export const publicRecipesQuerySchema = z
  .object({
    q: optionalQueryString,
    sort: z.enum(["newest", "oldest"]).default("newest"),
    type: recipeTypeSchema.or(z.literal("all")).default("all"),
    quality: z.preprocess(
      (value) => value === true || value === "true",
      z.boolean().default(false),
    ),
    caloriesMin: optionalNonNegativeNumber,
    caloriesMax: optionalNonNegativeNumber,
    createdBy: optionalQueryString,
    ingredient: optionalQueryString,
    cursor: optionalQueryString,
    limit: z.preprocess(
      (value) =>
        value === undefined || value === null || value === ""
          ? undefined
          : Number(value),
      z.number().int().min(1).max(50).default(20),
    ),
  })
  .strict()
  .refine(
    ({ caloriesMin, caloriesMax }) =>
      caloriesMin === undefined ||
      caloriesMax === undefined ||
      caloriesMin <= caloriesMax,
    { message: "Minimum calories cannot exceed maximum calories" },
  );

export const recipeSummarySchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  type: recipeTypeSchema,
  description: z.string(),
  image: z.string().nullable(),
  isPublic: z.boolean(),
  isOwned: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  servingTemperatureC: z.number().nullable(),
  relativeSweetness: z.number().nonnegative(),
  fatPct: z.number().min(0).max(1),
  kcalPer100g: z.number().nonnegative(),
  totalWeight: z.number().nonnegative(),
  numIngredients: z.number().int().nonnegative(),
  likesCount: z.number().int().nonnegative(),
  viewsCount: z.number().int().nonnegative(),
  machine: machineSchema.nullable(),
});

export const publicRecipeSummarySchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  type: recipeTypeSchema,
  createdAt: z.string(),
  description: z.string(),
  image: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  createdByImage: z.string().nullable(),
  ingredientCount: z.number().int().nonnegative(),
  likesCount: z.number().int().nonnegative(),
  likedByUser: z.boolean(),
  viewsCount: z.number().int().nonnegative(),
  servingTemperatureC: z.number().nullable(),
  relativeSweetness: z.number().nonnegative(),
  kcalPer100g: z.number().nonnegative(),
  totalWeight: z.number().nonnegative(),
});

const componentRecipeSummarySchema = recipeSummarySchema.pick({
  fatPct: true,
  id: true,
  image: true,
  kcalPer100g: true,
  name: true,
  relativeSweetness: true,
  servingTemperatureC: true,
  slug: true,
  totalWeight: true,
  type: true,
});

export const recipeComponentSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("recipe"),
    componentRecipe: componentRecipeSummarySchema,
    role: componentRoleSchema,
    order: z.number().int().nonnegative(),
    quantityMode: componentQuantityModeSchema,
    quantity: z.string(),
  }),
  z.object({
    kind: z.literal("ingredient"),
    componentIngredient: ingredientSchema,
    role: componentRoleSchema,
    order: z.number().int().nonnegative(),
    quantity: z.string(),
  }),
]);

export const recipeIngredientSchema = z.object({
  ingredient: ingredientSchema,
  amountGrams: z.string(),
  order: z.number().int().nonnegative(),
  notes: z.string(),
  isInfusion: z.boolean(),
  evaporationPercentage: z.string(),
});

export const clonedFromRecipeSchema = z
  .object({
    id: z.number().int().positive(),
    slug: z.string().min(1),
    name: z.string().min(1),
  })
  .nullable();

export const recipeTextCalculationsSchema = z.object({
  totalWeight: z.number().nonnegative(),
  totalEnergyKcal: z.number().nonnegative(),
  totalPOD: z.number().nonnegative(),
  milkfatPercentage: z.number().nonnegative(),
  fatPercentage: z.number().nonnegative(),
  saturatedFatPercentage: z.number().nonnegative(),
  transFatPercentage: z.number().nonnegative(),
  msnfPercentage: z.number().nonnegative(),
  sugarPercentage: z.number().nonnegative(),
  sugarNonDairyPercentage: z.number().nonnegative(),
  stabilizersPercentage: z.number().nonnegative(),
  stabilizersPerWaterPercentage: z.number().nonnegative(),
  emulsifiersPercentage: z.number().nonnegative(),
  proteinPercentage: z.number().nonnegative(),
  carbohydratePercentage: z.number().nonnegative(),
  fiberPercentage: z.number().nonnegative(),
  totalSolidsPercentage: z.number().nonnegative(),
  totalAlcoholPercentage: z.number().nonnegative(),
  servingTemperature: z.number().nullable(),
});

export const recipeSchema = recipeSummarySchema.extend({
  createdBy: z.string().nullable(),
  createdByName: z.string(),
  createdByImage: z.string().nullable(),
  ingredientCount: z.number().int().nonnegative(),
  likedByUser: z.boolean(),
  instructions: z.string(),
  evaporationPercentage: z.number().min(0).max(1),
  ingredients: z.array(recipeIngredientSchema),
  components: z.array(recipeComponentSchema),
  clonedFromRecipe: clonedFromRecipeSchema,
  calculations: recipeTextCalculationsSchema,
});

export const recipeIngredientInputSchema =
  persistedRecipeIngredientModifierFieldsSchema.safeExtend({
    ingredientId: z.number().int().positive(),
    amountGrams: recipeIngredientAmountSchema,
    order: z.number().int().nonnegative(),
    notes: recipeIngredientNotesValueSchema.default(""),
  });

export const recipeComponentInputSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("recipe"),
    componentRecipeId: z.number().int().positive(),
    role: componentRoleSchema,
    order: z.number().int().nonnegative(),
    quantityMode: componentQuantityModeSchema,
    quantity: componentQuantitySchema,
  }),
  z.object({
    kind: z.literal("ingredient"),
    componentIngredientId: z.number().int().positive(),
    role: componentRoleSchema,
    order: z.number().int().nonnegative(),
    quantity: componentQuantitySchema,
  }),
]);

export const recipeWriteSchema = z
  .object({
    name: trimStringInput(recipeNameSchema),
    type: recipeTypeSchema,
    description: recipeDescriptionSchema.default(""),
    instructions: recipeInstructionsSchema.default(""),
    ingredients: z
      .array(recipeIngredientInputSchema)
      .max(MAX_RECIPE_INGREDIENTS, maxRecipeIngredientsMessage),
    components: z
      .array(recipeComponentInputSchema)
      .max(MAX_RECIPE_INGREDIENTS, maxRecipeIngredientsMessage)
      .default([]),
    isPublic: z.boolean().default(false),
    evaporationPercentage: evaporationFractionSchema.default(0),
    image: z.string().nullable().default(null),
    machineId: z.number().int().positive().nullable().default(null),
    clonedFromRecipeId: z.number().int().positive().nullable().optional(),
  })
  .superRefine((value, context) => {
    if (value.type === "composite" && value.ingredients.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Composite recipes use components instead of ingredients",
        path: ["ingredients"],
      });
    }
    if (value.type !== "composite" && value.components.length > 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Only composite recipes can contain components",
        path: ["components"],
      });
    }
  });

export const recipesResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.array(recipeSummarySchema),
  page: z.object({
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export const publicRecipesResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.array(publicRecipeSummarySchema),
  page: z.object({
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export const recipeResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: recipeSchema,
});

export const deleteRecipeResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({ deleted: z.literal(true), slug: z.string() }),
});

export const recipeLikeResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({
    recipeId: z.number().int().positive(),
    likedByUser: z.boolean(),
    likesCount: z.number().int().nonnegative(),
  }),
});

export const recipeViewResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({
    recipeId: z.number().int().positive(),
    viewsCount: z.number().int().nonnegative(),
  }),
});

export type RecipesQuery = z.input<typeof recipesQuerySchema>;
export type ParsedRecipesQuery = z.output<typeof recipesQuerySchema>;
export type RecipeCollectionQuery = z.input<typeof recipeCollectionQuerySchema>;
export type PublicRecipesQuery = z.input<typeof publicRecipesQuerySchema>;
export type ParsedPublicRecipesQuery = z.output<
  typeof publicRecipesQuerySchema
>;
export type RecipeSummaryDto = z.infer<typeof recipeSummarySchema>;
export type RecipeDto = z.infer<typeof recipeSchema>;
export type RecipeComponentDto = z.infer<typeof recipeComponentSchema>;
export type RecipeTextCalculationsDto = z.infer<
  typeof recipeTextCalculationsSchema
>;
export type RecipeWrite = z.input<typeof recipeWriteSchema>;
export type ParsedRecipeWrite = z.output<typeof recipeWriteSchema>;
export type RecipesResponse = z.infer<typeof recipesResponseSchema>;
export type PublicRecipeSummaryDto = z.infer<typeof publicRecipeSummarySchema>;
export type PublicRecipesResponse = z.infer<typeof publicRecipesResponseSchema>;
export type RecipeResponse = z.infer<typeof recipeResponseSchema>;
export type DeleteRecipeResponse = z.infer<typeof deleteRecipeResponseSchema>;
export type RecipeLikeResponse = z.infer<typeof recipeLikeResponseSchema>;
export type RecipeViewResponse = z.infer<typeof recipeViewResponseSchema>;
